import React, { useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { motion } from 'framer-motion';
import { Terminal, Lock, Container, ShieldCheck } from 'lucide-react';
import { FormikInput } from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import SEO from '../../components/shared/SEO';

const LoginSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Required'),
  password: Yup.string().required('Required'),
});

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [fromCli , setFromCli] = useState(false)

  useEffect(() => {
    // SAFETY CHECK: 
    // If the returnUrl points to "login", clear it immediately. 
    // This breaks the infinite loop.
    const returnUrl = searchParams.get('returnUrl');
    if(returnUrl)setFromCli(true)
    if (returnUrl && returnUrl.includes('/login')) {
      console.warn("⚠️ Detected redirect loop. Clearing returnUrl.");
      setSearchParams({}); // Clear the params
    }
  }, [searchParams, setSearchParams]);

  const handleLogin = async (values, { setSubmitting }) => {
    try {
      const { ...apiData } = values;

      // Use Helper Function
      const data = await authAPI.login(apiData);

      login(data); // Save to context
      const returnUrl = searchParams.get('returnUrl');

      if (returnUrl) {
        // Decode it (it handles the %2F %3F characters)
        const target = decodeURIComponent(returnUrl);
        console.log("Redirecting back to:", target);
        navigate(target);
      } else {
        // Default behavior (if just logging in normally)
        navigate('/dashboard/home');
      }
    } catch (error) {
      // Error handled by Interceptor
      console.log("ERROR", error);
    } finally {
      setSubmitting(false);
    }
  };

return (
  <>
  <SEO title="Authorize | Access Your Vault" description="Securely sign in to your EnvDock account to manage your project environment variables." />
 
  <div className="min-h-screen flex items-center justify-center bg-[#050505] text-white p-3 sm:p-4 relative overflow-hidden font-sans">
    
    {/* --- Background Effects --- */}
    <div className="absolute inset-0 z-0 pointer-events-none">
      
      {/* Blur blob responsive */}
      <div className="
        absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
        w-[320px] h-[320px]
        sm:w-[500px] sm:h-[500px]
        md:w-[600px] md:h-[600px]
        bg-orange-600/10 rounded-full blur-[100px] sm:blur-[120px] opacity-50
      " />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:28px_28px] sm:bg-[size:32px_32px]" />

      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20" />
    </div>

    {/* --- Card --- */}
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="
        w-full max-w-md
        bg-[#0d0d0d]/80 backdrop-blur-2xl
        border border-white/10
        p-6 sm:p-8 md:p-10
        rounded-3xl sm:rounded-[2.5rem]
        shadow-2xl relative z-10
      "
    >

      {/* Logo / Icon Area */}
      <div className="flex flex-col items-center mb-8 sm:mb-10">

        <Link to="/" className="mb-5 sm:mb-6 group">
          <div className="
            w-12 h-12 sm:w-14 sm:h-14
            bg-gradient-to-br from-orange-500 to-red-600
            rounded-xl sm:rounded-2xl
            flex items-center justify-center
            shadow-lg shadow-orange-500/20
            group-hover:scale-110 transition-transform
          ">
            <Container size={24} className="text-white sm:w-7 sm:h-7" strokeWidth={2.5} />
          </div>
        </Link>

        <h2 className="text-2xl sm:text-3xl font-black tracking-tighter text-white mb-2 text-center">
          Welcome Back
        </h2>

        <p className="text-gray-500 text-xs sm:text-sm font-medium text-center">
          Access your EnvDock CLI Dashboard
        </p>
      </div>

      <Formik
        initialValues={{ email: '', password: '' }}
        validationSchema={LoginSchema}
        onSubmit={handleLogin}
      >
        {({ isSubmitting }) => (
          <Form className="space-y-4 sm:space-y-5">

            <FormikInput 
              name="email" 
              label="Email Address" 
              placeholder="name@company.com" 
              type="email" 
              className="bg-white/5 border-white/10 focus:border-orange-500/50"
            />

            <div className="relative">
              
              <FormikInput 
                name="password" 
                label="Password" 
                placeholder="••••••••" 
                type="password" 
                className="bg-white/5 border-white/10 focus:border-orange-500/50"
              />

              <Link 
                to="/forgot" 
                className="
                  absolute top-0 right-0
                  text-[10px] sm:text-[11px]
                  font-bold uppercase tracking-widest
                  text-orange-500 hover:text-orange-400
                  transition-colors
                "
              >
                Forgot?
              </Link>
            </div>

            {/* Button */}
            <Button 
              type="submit"
              disabled={isSubmitting}
              className="
                w-full
                h-12 sm:h-14
                mt-3 sm:mt-4
                bg-orange-600 hover:bg-orange-500
                text-white
                font-black
                text-base sm:text-lg
                rounded-xl sm:rounded-2xl
                transition-all
                shadow-xl shadow-white/5
              "
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                  Authenticating...
                </div>
              ) : (
                'Sign In'
              )}
            </Button>

          </Form>
        )}
      </Formik>

      {/* Footer */}
    {!fromCli &&  <div className="mt-8 sm:mt-10 pt-6 sm:pt-8 border-t border-white/5 text-center">
        <p className="text-gray-500 text-xs sm:text-sm">
          Don't have an account yet?{' '}
          <Link to="/register" className="text-orange-500 hover:text-orange-400 font-bold transition-colors">
            Sign up
          </Link>
        </p>
      </div>}

    </motion.div>
  </div>
   </>
);
}